import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { formatCurrency, formatDate, formatDateOnly } from '@/utils/helpers';
import type { SwingTradeAdjustement, LongTermAdjustement } from '@/models';
import type { GraphPoint } from '@/modelsAPI';

interface SymbolPriceChartProps {
  points: GraphPoint[];
  currency?: string;
  height?: number;
  strikePrice?: number;
  adjustements?: SwingTradeAdjustement[] | LongTermAdjustement[];
  averagePrice?: number;
  takeProfit?: number;
}

interface ChartDataPoint {
  date: string;
  dateDisplay: string;
  dateOnly: string;
  timestamp: number;
  price: number;
  volume: number | null;
  adjustment?: {
    delta: number;
    price: number;
  };
}

interface TooltipPayload {
  payload: ChartDataPoint;
}

interface TooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
}

// Chart styling constants
const CHART_COLORS = {
  positive: '#10b981',
  negative: '#ef4444',
  averagePrice: '#6b7280',
  takeProfitLine: '#10b981',
  strikeLine: '#ef4444',
  dotStroke: '#ffffff'
} as const;

const CHART_CONFIG = {
  dotRadius: 4,
  dotStrokeWidth: 1.5
} as const;

// Helper function to find the closest data point by timestamp (unix seconds)
function findClosestDataPointIndex(chartData: ChartDataPoint[], targetTimestamp: string): number {
  const targetTime = new Date(targetTimestamp).getTime();
  let closestIndex = 0;
  let closestDiff = Infinity;

  chartData.forEach((point, index) => {
    const pointTime = point.timestamp * 1000; // Convert unix seconds to ms
    const diff = Math.abs(pointTime - targetTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = index;
    }
  });

  return closestIndex;
}

// Helper function to integrate adjustements into chart data
function integrateAdjustements(chartData: ChartDataPoint[], adjustements: SwingTradeAdjustement[] | LongTermAdjustement[]): void {
  adjustements.forEach((adj) => {
    const closestIndex = findClosestDataPointIndex(chartData, adj.timestamp);

    if (chartData[closestIndex] !== undefined) {
      chartData[closestIndex].adjustment = {
        delta: 'delta' in adj ? adj.delta : 0,
        price: adj.price / 100 // Convert cents to dollars
      };
    }
  });
}

export default function SymbolPriceChart({ points, currency = 'EUR', height = 400, strikePrice, adjustements, averagePrice, takeProfit }: SymbolPriceChartProps) {
  const isSwingMode = adjustements !== undefined && averagePrice !== undefined;

  // Transform GraphPoint data for chart
  // Backend returns data in chronological order, no need to reverse
  const chartData: ChartDataPoint[] = points
    .filter((point) => point.p !== null)
    .map((point) => {
      const date = new Date(point.t * 1000); // Unix timestamp to JS Date
      return {
        date: date.toISOString(),
        dateDisplay: formatDate(date.toISOString()),
        dateOnly: formatDateOnly(date.toISOString()),
        timestamp: point.t,
        price: point.p / 100, // CurrencyCent to decimal
        volume: point.v,
      };
    });

  // Integrate adjustements into chart data for swing mode
  if (isSwingMode && adjustements) {
    integrateAdjustements(chartData, adjustements);
  }

  // Calculate unique ticks for XAxis (one per day) using the unique 'date' field
  const dailyTicks = useMemo(() => {
    const ticks: string[] = [];
    const seen = new Set<string>();
    for (const d of chartData) {
      if (!seen.has(d.dateOnly)) {
        seen.add(d.dateOnly);
        ticks.push(d.date);
      }
    }
    return ticks;
  }, [chartData]);

  const CustomTooltip = ({ active, payload }: TooltipProps) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            {data.dateOnly}
          </p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between items-center gap-4">
              <span className="text-gray-600 dark:text-gray-400">Kurs:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatCurrency(data.price, currency)}
              </span>
            </div>
            {data.volume !== null && (
              <div className="flex justify-between items-center gap-4">
                <span className="text-gray-600 dark:text-gray-400">Volumen:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {data.volume.toLocaleString('de-DE')}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom dot renderer for adjustments in swing mode
  const renderDot = isSwingMode
    ? (props: { cx?: number; cy?: number; payload?: ChartDataPoint }) => {
        const { cx, cy, payload } = props;

        if (!payload?.adjustment) {
          return <></>;
        }

        const color = payload.adjustment.delta >= 0
          ? CHART_COLORS.positive
          : CHART_COLORS.negative;

        return (
          <circle
            cx={cx}
            cy={cy}
            r={CHART_CONFIG.dotRadius}
            fill={color}
            stroke={CHART_COLORS.dotStroke}
            strokeWidth={CHART_CONFIG.dotStrokeWidth}
          />
        );
      }
    : false;

  // Calculate Y-axis domain - include takeProfit/strikePrice if present
  let yDomain: [string | number, string | number] = ['dataMin', 'dataMax'];
  if (chartData.length > 0) {
    const prices = chartData.map(d => d.price);

    if (isSwingMode && takeProfit !== undefined) {
      // Swing mode: include takeProfit and averagePrice
      const allValues = [...prices, takeProfit];
      if (averagePrice !== undefined) {
        allValues.push(averagePrice);
      }
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const padding = (max - min) * 0.1;
      yDomain = [min - padding, max + padding];
    } else if (!isSwingMode && strikePrice !== undefined) {
      // Core/Earnings mode: include strikePrice
      const allValues = [...prices, strikePrice];
      const min = Math.min(...allValues);
      const max = Math.max(...allValues);
      const padding = (max - min) * 0.1;
      yDomain = [min - padding, max + padding];
    }
  }

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <p className="text-lg font-medium">Keine Daten verfügbar</p>
          <p className="text-sm">Für die Darstellung des Charts sind historische Daten erforderlich.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <defs>
            <linearGradient id="strikeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART_COLORS.strikeLine} stopOpacity={0.3} />
              <stop offset="100%" stopColor={CHART_COLORS.strikeLine} stopOpacity={0.1} />
            </linearGradient>
          </defs>

          <CartesianGrid
            strokeDasharray="3 3"
            className="stroke-gray-200 dark:stroke-gray-700"
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
            tickMargin={8}
            minTickGap={30}
            interval="preserveStart"
            tickFormatter={(value) => formatDateOnly(value)}
            ticks={dailyTicks}
          />
          <YAxis
            tick={{ fontSize: 12, fill: 'currentColor' }}
            className="text-gray-600 dark:text-gray-400"
            tickFormatter={(value) => formatCurrency(value, currency)}
            tickMargin={8}
            domain={yDomain}
          />
          <Tooltip content={<CustomTooltip />} />

          {!isSwingMode && strikePrice !== undefined && (
            <>
              <ReferenceArea
                y1={strikePrice}
                y2={yDomain[0]}
                fill="url(#strikeGradient)"
                fillOpacity={1}
                ifOverflow="hidden"
              />

              <ReferenceLine
                y={strikePrice}
                stroke={CHART_COLORS.strikeLine}
                strokeDasharray="5 5"
                strokeWidth={1}
              />
            </>
          )}

          {isSwingMode && averagePrice !== undefined && (
            <ReferenceLine
              y={averagePrice}
              stroke={CHART_COLORS.averagePrice}
              strokeDasharray="3 3"
              strokeWidth={1.5}
            />
          )}

          {isSwingMode && takeProfit !== undefined && (
            <ReferenceLine
              y={takeProfit}
              stroke={CHART_COLORS.takeProfitLine}
              strokeDasharray="3 3"
              strokeWidth={1.5}
            />
          )}

          <Line
            type="monotone"
            dataKey="price"
            stroke="#0891b2"
            strokeWidth={2}
            dot={renderDot}
            activeDot={{ r: 5, fill: '#0891b2' }}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
